import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { AddShoppingCart } from '@mui/icons-material';
import {
	Alert,
	Box,
	Button,
	Chip,
	LinearProgress,
	ListItemText,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material';

import moment from 'moment';

import { currency, enqueueSnackbarError, enqueueSnackbarSuccess } from 'lib/helpers';
import { NfcCardOrder, NfcCardOrderStatus, cancelCardOrder } from 'lib/models/nfcCards';

import useIsMobile from 'hooks/useIsMobile';
import useNfcCard from 'hooks/useNfcCard';
import useNfcCardLabels from 'hooks/useNfcCardLabels';
import useNfcCardOrders from 'hooks/useNfcCardOrders';

import ConfirmationDialog from 'components/ConfirmationDialog';
import NfcCardHeader from 'components/NfcCardHeader';
import NfcCardProductsModal from 'components/NfcCardProductsModal';
import ResponsiveTable from 'components/ResponsiveTable';
import WrappingPagination from 'components/ResponsiveTable/WrappingPagination';
import ScreenContainer from 'components/containers/ScreenContainer';

const rowsPerPageOptions = [10, 25, 50];

const DATE_TIME_FORMAT = 'DD/MM/YYYY HH:mm';

const orderStatusColors: Record<NfcCardOrderStatus, 'warning' | 'success' | 'default'> = {
	active: 'warning',
	closed: 'success',
	cancelled: 'default',
};

/**
 * What was loaded on one card: which loads are still waiting at the bar, which were picked
 * up and which were cancelled. From here more products are loaded on the card, and a load
 * that is still pending is cancelled.
 */
export default function NfcCardOrdersScreen() {
	const { t } = useTranslation();
	const isMobile = useIsMobile();
	const { orderStatusLabel } = useNfcCardLabels();

	const { cardId = '' } = useParams<{ cardId: string }>();

	const { card, loading: loadingCard } = useNfcCard(cardId);

	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);

	const [showLoad, setShowLoad] = useState(false);
	const [showCancelConfirmation, setShowCancelConfirmation] = useState<NfcCardOrder | null>(null);
	const [cancelling, setCancelling] = useState(false);

	const { loading, orders, total, failed, refetch } = useNfcCardOrders(cardId, page, rowsPerPage);

	const onLoadClose = (loaded: boolean) => {
		setShowLoad(false);
		if (loaded) {
			// The new load is the newest order, so it is on the first page
			setPage(0);
			refetch();
		}
	};

	const onCancelHandler = async (mustCancel: boolean) => {
		const order = showCancelConfirmation;
		setShowCancelConfirmation(null);

		if (!mustCancel || !order) return;

		setCancelling(true);
		try {
			await cancelCardOrder(cardId, order._id);
			enqueueSnackbarSuccess(t('nfc:orderCancelled'));
		} catch (error) {
			enqueueSnackbarError(error);
		}
		setCancelling(false);
		// Refetched either way: when the cancellation is refused the order most likely moved on
		refetch();
	};

	const products = (order: NfcCardOrder) => (
		<Box>
			{order.products.map((product) => (
				<Typography key={product.productId} variant="body2">
					{product.quantity} x {product.title}
					{product.mixers?.length ? ` (${product.mixers.map((mixer) => mixer.mixer).join(', ')})` : ''}
				</Typography>
			))}
		</Box>
	);

	/** The status, with when the order was picked up or cancelled. */
	const status = (order: NfcCardOrder) => {
		const date =
			order.status === 'closed' ? order.confirmationDate : order.status === 'cancelled' ? order.cancellationDate : null;

		return (
			<Box>
				<Chip size="small" label={orderStatusLabel(order.status)} color={orderStatusColors[order.status]} />
				{!!date && (
					<Typography variant="caption" component="div">
						{moment(date).format(DATE_TIME_FORMAT)}
					</Typography>
				)}
			</Box>
		);
	};

	const createdAt = (order: NfcCardOrder) => (order.createdAt ? moment(order.createdAt).format(DATE_TIME_FORMAT) : '-');

	const actions = (order: NfcCardOrder) =>
		order.status === 'active' ? (
			<Button color="error" onClick={() => setShowCancelConfirmation(order)} disabled={cancelling}>
				{t('nfc:cancelOrder')}
			</Button>
		) : null;

	const pagination = {
		count: total,
		page,
		rowsPerPage,
		rowsPerPageOptions,
		onPageChange: (_: unknown, newPage: number) => setPage(newPage),
		onRowsPerPageChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			setRowsPerPage(parseInt(e.target.value, 10));
			setPage(0);
		},
	};

	// On a phone it goes after the loads and sticks to the bottom of the screen, where the thumb reaches it
	const loadButton = (
		<Box
			sx={{
				position: isMobile ? 'sticky' : 'static',
				bottom: 0,
				zIndex: 1,
				backgroundColor: 'background.default',
				paddingY: 1,
				marginBottom: 1,
			}}
		>
			{/* The API refuses to load a blocked or expired card; the button only spares the attempt */}
			<Button
				variant="contained"
				size="large"
				fullWidth={isMobile}
				startIcon={<AddShoppingCart />}
				onClick={() => setShowLoad(true)}
				disabled={card?.state !== 'active'}
				sx={{ minHeight: 48 }}
			>
				{t('nfc:loadProductsOne')}
			</Button>
		</Box>
	);

	return (
		<ScreenContainer title={t('nfc:orders')}>
			{showLoad && <NfcCardProductsModal cardIds={[cardId]} onClose={onLoadClose} />}

			{showCancelConfirmation && (
				<ConfirmationDialog
					title={t('nfc:cancelOrderTitle')}
					description={t('nfc:cancelOrderDescription', {
						code: showCancelConfirmation.code || showCancelConfirmation._id,
					})}
					confirmButton={t('nfc:cancelOrder')}
					onClose={onCancelHandler}
				/>
			)}

			<NfcCardHeader card={card} loading={loadingCard} />

			{!isMobile && loadButton}

			{failed && (
				<Alert severity="error" sx={{ marginBottom: 1 }}>
					{t('nfc:ordersFailed')}
				</Alert>
			)}

			{(loading || cancelling) && <LinearProgress />}

			{!loading && !failed && orders.length === 0 && (
				<Typography variant="body2" sx={{ paddingY: 2 }}>
					{t('nfc:ordersEmpty')}
				</Typography>
			)}

			<ResponsiveTable
				elements={orders}
				tableSx={{ maxHeight: '65vh' }}
				list={{
					primaryKey: '_id',
					renderListItemText: (order) => (
						<ListItemText
							primary={
								<Box>
									{products(order)}
									<Typography>{currency(order.total)}</Typography>
									{status(order)}
									<Typography variant="caption" component="div">
										{t('nfc:orderDate')}: {createdAt(order)}
									</Typography>
								</Box>
							}
							secondary={actions(order)}
							secondaryTypographyProps={{ component: 'div' }}
						/>
					),
				}}
				table={{
					renderHead: () => (
						<TableHead>
							<TableRow>
								<TableCell align="left" padding="normal">
									{t('nfc:products')}
								</TableCell>
								<TableCell align="left" padding="normal">
									{t('nfc:total')}
								</TableCell>
								<TableCell align="left" padding="normal">
									{t('nfc:orderStatus')}
								</TableCell>
								<TableCell align="left" padding="normal">
									{t('nfc:orderDate')}
								</TableCell>
								<TableCell align="left" padding="normal">
									{t('common:actions')}
								</TableCell>
							</TableRow>
						</TableHead>
					),
					renderRow: (order) => (
						<TableRow tabIndex={-1} key={order._id}>
							<TableCell>{products(order)}</TableCell>
							<TableCell>{currency(order.total)}</TableCell>
							<TableCell>{status(order)}</TableCell>
							<TableCell>{createdAt(order)}</TableCell>
							<TableCell>{actions(order)}</TableCell>
						</TableRow>
					),
					pagination,
				}}
			/>

			{/* The list ResponsiveTable renders on mobile has no pagination of its own, and the API pages the orders */}
			{isMobile && <WrappingPagination {...pagination} />}

			{isMobile && loadButton}
		</ScreenContainer>
	);
}
